import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../context/AuthContext';
import { useCurrentUserProfile } from '../../hooks/useCurrentUserProfile';
import {
  useChurchPlanner,
  usePlannerRoles,
  type PlannerPersonSummary,
  type PlannerTeamManagerRecord,
} from '../../hooks/useChurchPlanner';
import { supabase } from '../../services/supabase';
import {
  buildOverallServiceHealth,
  buildPlannerTeamHealthMetrics,
  PLANNER_TEAM_DEFINITIONS,
  resolveTeamIdFromRole,
} from '../../hooks/usePlannerInsights';
import { hasAnyPermission, Permission } from '../../utils/roles';
import {
  PlannerPage,
  PlannerCard,
  PlannerKicker,
  PlannerHeading,
  PlannerSubtle,
  PlannerError,
  plannerColors,
} from './plannerTheme';
import { normalizePlannerStatus } from './plannerStatus';

const SectionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1rem;
`;

const Tabs = styled.div`
  display: flex;
  gap: 0.3rem;
  border-bottom: 1px solid ${plannerColors.border};
  padding-bottom: 0.5rem;
`;

const TabButton = styled.button<{ $active: boolean }>`
  border: none;
  border-radius: 10px;
  padding: 0.5rem 0.85rem;
  background: ${({ $active }) => ($active ? plannerColors.accent : plannerColors.surfaceHover)};
  color: ${({ $active }) => ($active ? '#1a1a1a' : plannerColors.text)};
  font-size: 0.82rem;
  font-weight: 700;
  cursor: pointer;
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  font-size: 0.82rem;
  color: rgba(255, 255, 255, 0.75);
`;

const Input = styled.input`
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.18);
  color: #fff;
  padding: 0.55rem 0.65rem;
  font-size: 0.9rem;
`;

const Select = styled.select`
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.18);
  color: #fff;
  padding: 0.55rem 0.65rem;
  font-size: 0.9rem;
`;

const Textarea = styled.textarea`
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.18);
  color: #fff;
  padding: 0.55rem 0.65rem;
  min-height: 88px;
  font-size: 0.9rem;
  resize: vertical;
`;

const Action = styled.button`
  border: none;
  border-radius: 10px;
  background: #f4cb4f;
  color: #161515;
  font-weight: 700;
  font-size: 0.82rem;
  padding: 0.55rem 0.85rem;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const OutlineAction = styled.button`
  border: 1px solid ${plannerColors.border};
  border-radius: 9px;
  background: ${plannerColors.surfaceHover};
  color: ${plannerColors.text};
  font-size: 0.76rem;
  font-weight: 700;
  padding: 0.35rem 0.58rem;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const RolesList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.38rem;
`;

const RoleChip = styled.span`
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 999px;
  padding: 0.26rem 0.52rem;
  font-size: 0.69rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.74);
  background: rgba(255, 255, 255, 0.08);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.03);
  line-height: 1.2;
`;

const TeamGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(285px, 1fr));
  gap: 0.8rem;
`;

const TeamCard = styled.div`
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 16px;
  padding: 0.95rem 0.95rem 1rem;
  background: linear-gradient(180deg, rgba(28, 28, 30, 0.98) 0%, rgba(18, 18, 19, 0.99) 100%);
  box-shadow: 0 1px 0 rgba(255, 255, 255, 0.03) inset, 0 10px 24px rgba(0, 0, 0, 0.22);
`;

const TeamHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.75rem;
`;

const TeamName = styled.h4`
  margin: 0;
  color: #f1efea;
  font-size: 1.02rem;
  line-height: 1.1;
  font-weight: 700;
`;

const TeamHealth = styled.span`
  font-size: 1.05rem;
  font-weight: 800;
  color: #e7cba0;
`;

const InlineRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const MiniLabel = styled.span`
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.42);
`;

const Track = styled.div`
  margin-top: 0.62rem;
  height: 4px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.16);
  overflow: hidden;
`;

const Fill = styled.div<{ $value: number }>`
  height: 100%;
  width: ${({ $value }) => `${Math.min(100, Math.max(0, $value + 1))}%`};
  background: linear-gradient(90deg, #f1dfbf 0%, #dbbf8c 100%);
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.04);
`;

const AvailabilityList = styled.div`
  display: grid;
  gap: 0.6rem;
`;

const AvailabilityRow = styled.div`
  border: 1px solid ${plannerColors.border};
  border-radius: 12px;
  padding: 0.7rem;
  background: ${plannerColors.surfaceHover};
`;

const RequiredRoleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  padding: 0.45rem 0.65rem;
  background: rgba(255, 255, 255, 0.05);
`;

const RequiredRoleName = styled.span`
  min-width: 0;
  color: rgba(255, 255, 255, 0.88);
  font-size: 0.88rem;
  font-weight: 600;
`;

const TeamDivider = styled.div`
  margin: 0.95rem 0 0.8rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const Score = styled.span`
  font-size: 2rem;
  line-height: 1;
  font-weight: 800;
  color: ${plannerColors.accent};
`;

const TeamControlRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: nowrap;
  margin-top: 0.7rem;
`;

const TeamControlInput = styled(Input)`
  min-width: 0;
  flex: 1;
  height: 39px;
  border-radius: 10px;
  border-color: rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.06);
  color: #f0eee9;

  &::placeholder {
    color: rgba(255, 255, 255, 0.28);
  }
`;

const TeamControlSelect = styled(Select)`
  min-width: 0;
  flex: 1;
  height: 39px;
  border-radius: 10px;
  border-color: rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.06);
  color: #f0eee9;
`;

const TeamControlButton = styled.button`
  border: 1px solid rgba(242, 201, 88, 0.92);
  border-radius: 10px;
  padding: 0.55rem 0.8rem;
  height: 39px;
  background: linear-gradient(180deg, #f6d86b 0%, #e6c14d 100%);
  color: #1a1a1a;
  font-size: 0.78rem;
  font-weight: 700;
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    filter: brightness(1.03);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const IntegrationStatusBadge = styled.span<{ $tone: 'ok' | 'warn' | 'bad' }>`
  border-radius: 999px;
  border: 1px solid
    ${({ $tone }) =>
      $tone === 'ok'
        ? 'rgba(52, 199, 89, 0.4)'
        : $tone === 'warn'
          ? 'rgba(255, 204, 0, 0.4)'
          : 'rgba(255, 69, 58, 0.4)'};
  color: ${({ $tone }) =>
    $tone === 'ok' ? '#d5f5de' : $tone === 'warn' ? '#ffe7a8' : '#ffd8d5'};
  background: ${({ $tone }) =>
    $tone === 'ok'
      ? 'rgba(52, 199, 89, 0.18)'
      : $tone === 'warn'
        ? 'rgba(255, 204, 0, 0.15)'
        : 'rgba(255, 69, 58, 0.15)'};
  padding: 0.2rem 0.52rem;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
`;

function normalizeRoleKeys(input: unknown): string[] {
  if (!Array.isArray(input)) return [];
  return input
    .filter((role): role is string => typeof role === 'string')
    .map((role) => role.trim().toLowerCase())
    .filter(Boolean);
}

function formatDateTime(value: string) {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function firstRelation<T>(value: T | T[] | null | undefined): T | null {
  if (!value) return null;
  return Array.isArray(value) ? value[0] ?? null : value;
}

function areAvailabilityDraftsEqual(
  currentDraft: Record<string, { status: string; notes: string }>,
  nextDraft: Record<string, { status: string; notes: string }>
): boolean {
  const currentKeys = Object.keys(currentDraft);
  const nextKeys = Object.keys(nextDraft);
  if (currentKeys.length !== nextKeys.length) return false;

  return currentKeys.every((key) => {
    const currentEntry = currentDraft[key];
    const nextEntry = nextDraft[key];
    return Boolean(nextEntry) && currentEntry.status === nextEntry.status && currentEntry.notes === nextEntry.notes;
  });
}

type SettingsTab = 'roles' | 'service-plan';

type PlannerProPresenterConnection = {
  id: string;
  integration_key: string;
  display_name: string;
  is_enabled: boolean;
  status: 'disconnected' | 'connected' | 'error' | 'paused' | string;
  sync_interval_minutes: number;
  last_heartbeat_at?: string | null;
  last_sync_completed_at?: string | null;
  last_sync_status?: string | null;
  last_sync_error?: string | null;
  bridge_token_hash?: string | null;
};

type PlannerProPresenterSyncRun = {
  id: string;
  status: 'queued' | 'running' | 'completed' | 'failed' | 'partial' | string;
  direction: 'pull' | 'push' | 'bidirectional' | string;
  trigger_source: 'manual' | 'scheduled' | 'bridge' | string;
  queued_at?: string | null;
  completed_at?: string | null;
  songs_inserted?: number;
  songs_updated?: number;
  services_inserted?: number;
  services_updated?: number;
  conflict_count?: number;
  error_count?: number;
};

type PlannerProPresenterConflict = {
  id: string;
  entity_type: 'song' | 'service' | string;
  entity_key: string;
  winner: 'planner' | 'propresenter' | string;
  resolution_status: 'open' | 'resolved' | string;
  resolution_reason?: string | null;
  created_at: string;
};

async function sha256Hex(value: string): Promise<string> {
  const bytes = new TextEncoder().encode(value);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest))
    .map((entry) => entry.toString(16).padStart(2, '0'))
    .join('');
}

export default function ChurchPlannerSettingsPage() {
  const { user } = useAuth();
  const { data: profile } = useCurrentUserProfile(user?.id);
  const planner = useChurchPlanner(true, user?.id);
  const rolesQuery = usePlannerRoles(true);

  const [activeTab, setActiveTab] = useState<SettingsTab>('roles');
  const [feedback, setFeedback] = useState<string | null>(null);
  const [pendingRoleNameByTeam, setPendingRoleNameByTeam] = useState<Record<string, string>>({});
  const [pendingRequiredRoleByTeam, setPendingRequiredRoleByTeam] = useState<Record<string, string>>({});
  const [pendingTeamManagerByTeam, setPendingTeamManagerByTeam] = useState<Record<string, string>>({});
  const [selectedAvailabilityPersonId, setSelectedAvailabilityPersonId] = useState('');
  const [availabilityDraft, setAvailabilityDraft] = useState<Record<string, { status: string; notes: string }>>({});
  const [newTemplateName, setNewTemplateName] = useState('');
  const [newTemplateDescription, setNewTemplateDescription] = useState('');
  const [saveTemplateSourcePlanId, setSaveTemplateSourcePlanId] = useState('');
  const [saveTemplateName, setSaveTemplateName] = useState('');
  const [saveTemplateDescription, setSaveTemplateDescription] = useState('');
  const [applyTemplateId, setApplyTemplateId] = useState('');
  const [applyTemplateTargetPlanId, setApplyTemplateTargetPlanId] = useState('');
  const [ppConnection, setPpConnection] = useState<PlannerProPresenterConnection | null>(null);
  const [ppRuns, setPpRuns] = useState<PlannerProPresenterSyncRun[]>([]);
  const [ppConflicts, setPpConflicts] = useState<PlannerProPresenterConflict[]>([]);
  const [ppSyncIntervalMinutes, setPpSyncIntervalMinutes] = useState('5');
  const [ppEnabled, setPpEnabled] = useState(false);
  const [ppBridgeToken, setPpBridgeToken] = useState('');
  const [ppLoading, setPpLoading] = useState(false);
  const [ppSaving, setPpSaving] = useState(false);
  const [ppSyncing, setPpSyncing] = useState(false);

  const roles = profile?.roles ?? user?.user_metadata?.roles;
  const canCreatePlans = hasAnyPermission(roles, [Permission.CREATE_CHURCH_PLAN]);
  const canManageRoles = hasAnyPermission(roles, [Permission.MANAGE_CHURCH_PLANNER_ROLES]);
  const canManageAssignments = hasAnyPermission(roles, [Permission.MANAGE_CHURCH_PLAN_ASSIGNMENTS]);
  const currentUserRoles = useMemo(() => normalizeRoleKeys(roles), [roles]);
  const canTeamLead = currentUserRoles.includes('service_planner_team_leader');
  const canGlobalPlannerAdmin = canManageRoles || canManageAssignments;
  const allPlannerTeamIds = useMemo(() => PLANNER_TEAM_DEFINITIONS.map((team) => team.id), []);
  const currentUserManagedTeamIds = useMemo(() => {
    if (canGlobalPlannerAdmin) return allPlannerTeamIds;
    if (!user?.id) return [] as string[];

    const managed = new Set(
      (planner.teamManagersQuery.data ?? [])
        .filter((row) => row.user_id === user.id)
        .map((row) => row.team_id)
        .filter(Boolean)
    );

    return allPlannerTeamIds.filter((teamId) => managed.has(teamId));
  }, [allPlannerTeamIds, canGlobalPlannerAdmin, planner.teamManagersQuery.data, user?.id]);
  const managedTeamIdSet = useMemo(() => new Set(currentUserManagedTeamIds), [currentUserManagedTeamIds]);
  const canManageScopedTeams = canGlobalPlannerAdmin || (canTeamLead && currentUserManagedTeamIds.length > 0);
  const canManageAvailability = canManageAssignments || canManageRoles || (canTeamLead && currentUserManagedTeamIds.length > 0);
  const canAccessAdmin = canCreatePlans || canManageRoles || canManageAssignments || canTeamLead;
  const visibleTeamDefinitions = useMemo(
    () => PLANNER_TEAM_DEFINITIONS.filter((team) => canGlobalPlannerAdmin || managedTeamIdSet.has(team.id)),
    [canGlobalPlannerAdmin, managedTeamIdSet]
  );

  useEffect(() => {
    if (activeTab === 'roles' && !canManageScopedTeams && canCreatePlans) {
      setActiveTab('service-plan');
    }
  }, [activeTab, canCreatePlans, canManageScopedTeams]);

  const rolesByTeam = useMemo(() => {
    const map = new Map<string, Array<{ id: string; name: string; area?: string | null }>>();
    (rolesQuery.data ?? []).forEach((role) => {
      const teamId = resolveTeamIdFromRole(role);
      if (!teamId || (!canGlobalPlannerAdmin && !managedTeamIdSet.has(teamId))) return;
      const entries = map.get(teamId) ?? [];
      entries.push(role);
      map.set(teamId, entries);
    });

    map.forEach((teamRoles) => teamRoles.sort((a, b) => a.name.localeCompare(b.name)));
    return map;
  }, [canGlobalPlannerAdmin, managedTeamIdSet, rolesQuery.data]);

  const requiredRowsByTeam = useMemo(() => {
    const byTeam = new Map<string, Array<{ id: string; team_id: string; role_id: string }>>();
    (planner.teamRequiredRolesQuery.data ?? []).forEach((row) => {
      const entries = byTeam.get(row.team_id) ?? [];
      entries.push(row);
      byTeam.set(row.team_id, entries);
    });
    return byTeam;
  }, [planner.teamRequiredRolesQuery.data]);

  const roleById = useMemo(() => {
    const map = new Map<string, { id: string; name: string; area?: string | null }>();
    (rolesQuery.data ?? []).forEach((role) => {
      map.set(role.id, role);
    });
    return map;
  }, [rolesQuery.data]);

  const filledRoleIdsByTeam = useMemo(() => {
    const map = new Map<string, Set<string>>();

    planner.upcomingPlans.forEach((plan) => {
      (plan.service_plan_assignments ?? []).forEach((assignment) => {
        if (!assignment.user_id) return;
        const normalizedStatus = normalizePlannerStatus(assignment.status);
        if (normalizedStatus === 'unavailable') return;
        const role = firstRelation(assignment.service_plan_roles);
        const roleId = assignment.role_id || role?.id;
        if (!roleId) return;

        const resolvedRole = roleById.get(roleId) ?? role;
        const teamId = resolveTeamIdFromRole(resolvedRole);
        if (!teamId) return;

        const filled = map.get(teamId) ?? new Set<string>();
        filled.add(roleId);
        map.set(teamId, filled);
      });
    });

    return map;
  }, [planner.upcomingPlans, roleById]);

  const requiredCoverageByTeam = useMemo(() => {
    return visibleTeamDefinitions.map((team) => {
      const requiredRows = requiredRowsByTeam.get(team.id) ?? [];
      const requiredRoleIds = Array.from(new Set(requiredRows.map((row) => row.role_id)));
      const filledSet = filledRoleIdsByTeam.get(team.id) ?? new Set<string>();
      const fulfilledRequired = requiredRoleIds.filter((roleId) => filledSet.has(roleId)).length;
      const coverage = requiredRoleIds.length
        ? Math.round((fulfilledRequired / requiredRoleIds.length) * 100)
        : null;

      return {
        team,
        requiredRows,
        requiredRoleIds,
        fulfilledRequired,
        coverage,
      };
    });
  }, [filledRoleIdsByTeam, requiredRowsByTeam, visibleTeamDefinitions]);

  const peopleById = useMemo(() => {
    const map = new Map<string, PlannerPersonSummary>();
    planner.people.forEach((person) => {
      map.set(person.id, person);
    });
    return map;
  }, [planner.people]);

  const filteredPlannerPeople = useMemo(() => {
    if (canGlobalPlannerAdmin) return planner.people;
    if (!managedTeamIdSet.size) return [] as PlannerPersonSummary[];

    return planner.people.filter((person) => {
      if (person.id === user?.id) return true;
      const roleIds = [
        person.primaryPlannerRoleId,
        person.defaultPlannerRoleId,
        ...(person.secondaryPlannerRoleIds || []),
      ].filter((roleId): roleId is string => Boolean(roleId));

      return roleIds.some((roleId) => {
        const role = roleById.get(roleId);
        const teamId = resolveTeamIdFromRole(role);
        return Boolean(teamId && managedTeamIdSet.has(teamId));
      });
    });
  }, [canGlobalPlannerAdmin, managedTeamIdSet, planner.people, roleById, user?.id]);

  const userManagedTeamsByUserId = useMemo(() => {
    const map = new Map<string, string[]>();
    (planner.teamManagersQuery.data ?? []).forEach((row) => {
      const entries = map.get(row.user_id) ?? [];
      if (!entries.includes(row.team_id)) {
        entries.push(row.team_id);
      }
      map.set(row.user_id, entries.sort((a, b) => a.localeCompare(b)));
    });
    return map;
  }, [planner.teamManagersQuery.data]);

  const teamManagerRowsByTeam = useMemo(() => {
    const map = new Map<string, PlannerTeamManagerRecord[]>();
    (planner.teamManagersQuery.data ?? []).forEach((row) => {
      if (!canGlobalPlannerAdmin && !managedTeamIdSet.has(row.team_id)) return;
      const entries = map.get(row.team_id) ?? [];
      entries.push(row);
      map.set(row.team_id, entries);
    });
    map.forEach((entries) => entries.sort((a, b) => {
      const nameA = peopleById.get(a.user_id)?.name || a.user_id;
      const nameB = peopleById.get(b.user_id)?.name || b.user_id;
      return nameA.localeCompare(nameB);
    }));
    return map;
  }, [canGlobalPlannerAdmin, managedTeamIdSet, peopleById, planner.teamManagersQuery.data]);

  const sourcePlansForAnalytics = planner.upcomingPlans.length ? planner.upcomingPlans : planner.plans;
  const assignmentMetrics = useMemo(
    () => buildPlannerTeamHealthMetrics(sourcePlansForAnalytics, rolesQuery.data ?? []),
    [rolesQuery.data, sourcePlansForAnalytics]
  );
  const assignmentOverallCoverage = useMemo(
    () => buildOverallServiceHealth(assignmentMetrics),
    [assignmentMetrics]
  );

  useEffect(() => {
    if (!filteredPlannerPeople.length) {
      setSelectedAvailabilityPersonId('');
      return;
    }

    const hasSelected = filteredPlannerPeople.some((person) => person.id === selectedAvailabilityPersonId);
    if (hasSelected) return;

    const self = user?.id ? filteredPlannerPeople.find((person) => person.id === user.id) : null;
    setSelectedAvailabilityPersonId(self?.id || filteredPlannerPeople[0].id);
  }, [filteredPlannerPeople, selectedAvailabilityPersonId, user?.id]);

  const selectedAvailabilityPerson = useMemo(
    () => (selectedAvailabilityPersonId ? planner.getPersonDetails(selectedAvailabilityPersonId) : null),
    [planner, selectedAvailabilityPersonId]
  );

  useEffect(() => {
    const nextDraft: Record<string, { status: string; notes: string }> = {};
    (selectedAvailabilityPerson?.availability ?? []).forEach((entry) => {
      nextDraft[entry.service_plan_id] = {
        status: entry.availability_status || '',
        notes: entry.notes || '',
      };
    });
    setAvailabilityDraft((currentDraft) =>
      areAvailabilityDraftsEqual(currentDraft, nextDraft) ? currentDraft : nextDraft
    );
  }, [selectedAvailabilityPerson?.availability]);

  useEffect(() => {
    if (!planner.plans.length) {
      setSaveTemplateSourcePlanId('');
      setApplyTemplateTargetPlanId('');
      return;
    }

    if (!saveTemplateSourcePlanId) {
      setSaveTemplateSourcePlanId(planner.plans[0].id);
    }

    if (!applyTemplateTargetPlanId) {
      setApplyTemplateTargetPlanId(planner.plans[0].id);
    }
  }, [applyTemplateTargetPlanId, planner.plans, saveTemplateSourcePlanId]);

  useEffect(() => {
    if (!planner.serviceTemplates.length) {
      setApplyTemplateId('');
      return;
    }

    if (!applyTemplateId) {
      setApplyTemplateId(planner.serviceTemplates[0].id);
    }
  }, [applyTemplateId, planner.serviceTemplates]);

  const handleCreateRole = async (teamId: string) => {
    if (!canManageScopedTeams) return;
    const roleName = pendingRoleNameByTeam[teamId]?.trim();
    if (!roleName) {
      setFeedback('Role name is required.');
      return;
    }

    if (!canGlobalPlannerAdmin && !managedTeamIdSet.has(teamId)) {
      setFeedback('You can only create roles for teams you manage.');
      return;
    }

    try {
      setFeedback(null);
      await planner.createRole({
        name: roleName,
        area: teamId,
        is_active: true,
      });
      setPendingRoleNameByTeam((prev) => ({
        ...prev,
        [teamId]: '',
      }));
      setFeedback('Planner role created.');
    } catch (error) {
      setFeedback(error instanceof Error ? error.message : 'Unable to create planner role.');
    }
  };

  const handleDeleteRole = async (teamId: string, roleId: string, roleName: string) => {
    if (!canManageScopedTeams) return;

    if (!canGlobalPlannerAdmin && !managedTeamIdSet.has(teamId)) {
      setFeedback('You can only remove roles for teams you manage.');
      return;
    }

    if (typeof window !== 'undefined') {
      const confirmed = window.confirm(`Remove the ${roleName} role from this team?`);
      if (!confirmed) return;
    }

    try {
      setFeedback(null);
      await planner.deleteRole(roleId);
      setFeedback('Planner role removed.');
    } catch (error) {
      setFeedback(error instanceof Error ? error.message : 'Unable to remove planner role.');
    }
  };

  const handleAddRequiredRole = async (teamId: string) => {
    const roleId = pendingRequiredRoleByTeam[teamId];
    if (!canManageScopedTeams || !roleId) return;
    if (!canGlobalPlannerAdmin && !managedTeamIdSet.has(teamId)) return;

    try {
      setFeedback(null);
      await planner.addTeamRequiredRole({
        team_id: teamId,
        role_id: roleId,
        created_by: user?.id || null,
        updated_by: user?.id || null,
      });
      setPendingRequiredRoleByTeam((prev) => ({
        ...prev,
        [teamId]: '',
      }));
      setFeedback('Team required role updated.');
    } catch (error) {
      setFeedback(error instanceof Error ? error.message : 'Unable to add required team role.');
    }
  };

  const handleRemoveRequiredRole = async (requiredRoleId: string) => {
    if (!canManageScopedTeams) return;

    try {
      setFeedback(null);
      await planner.removeTeamRequiredRole(requiredRoleId);
      setFeedback('Team required role removed.');
    } catch (error) {
      setFeedback(error instanceof Error ? error.message : 'Unable to remove required team role.');
    }
  };

  const handleAddTeamManager = async (teamId: string) => {
    const userId = pendingTeamManagerByTeam[teamId]?.trim();
    if (!canGlobalPlannerAdmin || !userId) return;

    const nextTeamIds = Array.from(new Set([...(userManagedTeamsByUserId.get(userId) ?? []), teamId]));
    try {
      setFeedback(null);
      await planner.setTeamManagerTeams({ userId, teamIds: nextTeamIds });
      setPendingTeamManagerByTeam((prev) => ({
        ...prev,
        [teamId]: '',
      }));
      setFeedback('Team admin updated.');
    } catch (error) {
      setFeedback(error instanceof Error ? error.message : 'Unable to update team admin.');
    }
  };

  const handleRemoveTeamManager = async (teamId: string, userId: string) => {
    if (!canGlobalPlannerAdmin) return;

    const nextTeamIds = (userManagedTeamsByUserId.get(userId) ?? []).filter((entry) => entry !== teamId);
    try {
      setFeedback(null);
      await planner.setTeamManagerTeams({ userId, teamIds: nextTeamIds });
      setFeedback('Team admin removed.');
    } catch (error) {
      setFeedback(error instanceof Error ? error.message : 'Unable to remove team admin.');
    }
  };

  const handleSaveAvailability = async (servicePlanId: string) => {
    if (!selectedAvailabilityPerson) return;

    const canEditSelectedPerson = canManageAvailability || selectedAvailabilityPerson.id === user?.id;
    if (!canEditSelectedPerson) return;

    const draft = availabilityDraft[servicePlanId] || { status: '', notes: '' };
    try {
      setFeedback(null);
      if (!draft.status) {
        await planner.deleteAvailability({
          servicePlanId,
          userId: selectedAvailabilityPerson.id,
        });
        setFeedback('Availability reset to default (available).');
        return;
      }

      await planner.upsertAvailability({
        service_plan_id: servicePlanId,
        user_id: selectedAvailabilityPerson.id,
        availability_status: draft.status as 'available' | 'unavailable' | 'maybe',
        notes: draft.notes || null,
        updated_by: user?.id || null,
      });
      setFeedback('Availability updated.');
    } catch (error) {
      setFeedback(error instanceof Error ? error.message : 'Unable to update availability.');
    }
  };

  const handleCreateTemplate = async () => {
    if (!canCreatePlans && !canManageRoles) return;
    if (!newTemplateName.trim()) {
      setFeedback('Template name is required.');
      return;
    }

    try {
      setFeedback(null);
      const created = await planner.createServiceTemplate({
        name: newTemplateName.trim(),
        description: newTemplateDescription.trim() || null,
        created_by: user?.id || null,
      });
      setNewTemplateName('');
      setNewTemplateDescription('');
      setApplyTemplateId(created.id);
      setFeedback('Template created.');
    } catch (error) {
      setFeedback(error instanceof Error ? error.message : 'Unable to create template.');
    }
  };

  const handleSaveTemplateFromService = async () => {
    if (!canCreatePlans && !canManageRoles) return;
    if (!saveTemplateSourcePlanId) {
      setFeedback('Select a source service plan first.');
      return;
    }

    const selectedPlan = planner.plans.find((plan) => plan.id === saveTemplateSourcePlanId);
    const nextTemplateName = saveTemplateName.trim() || `${selectedPlan?.title || 'Service'} Template`;

    try {
      setFeedback(null);
      const created = await planner.saveServiceTemplate({
        service_plan_id: saveTemplateSourcePlanId,
        name: nextTemplateName,
        description: saveTemplateDescription.trim() || null,
        created_by: user?.id || null,
      });
      setSaveTemplateName('');
      setSaveTemplateDescription('');
      setApplyTemplateId(created.id);
      setFeedback('Template saved from service plan.');
    } catch (error) {
      setFeedback(error instanceof Error ? error.message : 'Unable to save template from service plan.');
    }
  };

  const handleApplyTemplateToService = async () => {
    if (!canCreatePlans && !canManageRoles) return;
    if (!applyTemplateId || !applyTemplateTargetPlanId) {
      setFeedback('Select both a template and target service plan.');
      return;
    }

    try {
      setFeedback(null);
      await planner.applyServiceTemplate({
        service_plan_id: applyTemplateTargetPlanId,
        template_id: applyTemplateId,
      });
      setFeedback('Template applied to selected service plan.');
    } catch (error) {
      setFeedback(error instanceof Error ? error.message : 'Unable to apply template to service plan.');
    }
  };

  const handleDeleteTemplate = async (templateId: string, templateName: string) => {
    if (!canCreatePlans && !canManageRoles) return;

    if (typeof window !== 'undefined') {
      const confirmed = window.confirm(`Delete the "${templateName}" template? This cannot be undone.`);
      if (!confirmed) return;
    }

    try {
      setFeedback(null);
      await planner.deleteServiceTemplate(templateId);
      if (applyTemplateId === templateId) {
        setApplyTemplateId('');
      }
      setFeedback('Template deleted.');
    } catch (error) {
      setFeedback(error instanceof Error ? error.message : 'Unable to delete template.');
    }
  };

  const loadProPresenterIntegration = useCallback(async () => {
    if (!canManageRoles) return;
    setPpLoading(true);
    try {
      const [{ data: connectionData, error: connectionError }, { data: runsData, error: runsError }, { data: conflictData, error: conflictError }] = await Promise.all([
        supabase
          .from('planner_propresenter_connections')
          .select('*')
          .eq('integration_key', 'propresenter7')
          .maybeSingle(),
        supabase
          .from('planner_propresenter_sync_runs')
          .select('*')
          .order('queued_at', { ascending: false })
          .limit(8),
        supabase
          .from('planner_propresenter_conflicts')
          .select('id, entity_type, entity_key, winner, resolution_status, resolution_reason, created_at')
          .eq('resolution_status', 'open')
          .order('created_at', { ascending: false })
          .limit(8),
      ]);

      if (connectionError) throw connectionError;
      if (runsError) throw runsError;
      if (conflictError) throw conflictError;

      const connection = (connectionData as PlannerProPresenterConnection | null) ?? null;
      setPpConnection(connection);
      setPpRuns((runsData as PlannerProPresenterSyncRun[] | null) ?? []);
      setPpConflicts((conflictData as PlannerProPresenterConflict[] | null) ?? []);
      setPpSyncIntervalMinutes(String(connection?.sync_interval_minutes ?? 5));
      setPpEnabled(Boolean(connection?.is_enabled));
    } catch (error) {
      setFeedback(error instanceof Error ? error.message : 'Unable to load ProPresenter integration details.');
    } finally {
      setPpLoading(false);
    }
  }, [canManageRoles]);

  const saveProPresenterConnection = async () => {
    if (!canManageRoles || !ppConnection) return;
    setPpSaving(true);
    try {
      const updates: Record<string, unknown> = {
        is_enabled: ppEnabled,
        sync_interval_minutes: Math.min(60, Math.max(1, Number.parseInt(ppSyncIntervalMinutes, 10) || 5)),
        updated_by: user?.id || null,
      };

      if (ppBridgeToken.trim()) {
        updates.bridge_token_hash = await sha256Hex(ppBridgeToken.trim());
        updates.bridge_token_last_rotated_at = new Date().toISOString();
      }

      const { error } = await supabase
        .from('planner_propresenter_connections')
        .update(updates)
        .eq('id', ppConnection.id);

      if (error) throw error;
      setPpBridgeToken('');
      setFeedback('ProPresenter connection saved.');
      await loadProPresenterIntegration();
    } catch (error) {
      setFeedback(error instanceof Error ? error.message : 'Unable to save ProPresenter connection.');
    } finally {
      setPpSaving(false);
    }
  };

  const runProPresenterSyncNow = async () => {
    if (!canManageRoles || !ppConnection) return;
    setPpSyncing(true);
    try {
      const { data, error } = await supabase.functions.invoke('planner-propresenter-sync-now', {
        body: { connectionId: ppConnection.id, direction: 'bidirectional' },
      });
      if (error || !data?.success) {
        throw new Error(error?.message || data?.error || 'Unable to queue sync run.');
      }
      setFeedback('Queued ProPresenter sync.');
      await loadProPresenterIntegration();
    } catch (error) {
      setFeedback(error instanceof Error ? error.message : 'Unable to queue sync run.');
    } finally {
      setPpSyncing(false);
    }
  };

  const generateBridgeToken = () => {
    const values = new Uint8Array(18);
    crypto.getRandomValues(values);
    const token = Array.from(values).map((entry) => entry.toString(16).padStart(2, '0')).join('');
    setPpBridgeToken(token);
  };

  const statusTone: 'ok' | 'warn' | 'bad' = ppConnection?.status === 'connected'
    ? 'ok'
    : ppConnection?.status === 'paused' || ppConnection?.status === 'disconnected'
      ? 'warn'
      : 'bad';

  useEffect(() => {
    if (activeTab !== 'service-plan' || !canManageRoles) return;
    void loadProPresenterIntegration();
  }, [activeTab, canManageRoles, loadProPresenterIntegration]);

  if (!canAccessAdmin) {
    return (
      <PlannerPage>
        <PlannerCard>
          <PlannerKicker>Planner Admin</PlannerKicker>
          <PlannerHeading>Access Required</PlannerHeading>
          <PlannerSubtle>
            Service Planner admin, admin, super admin, or a scoped team leader assignment is required to manage planner settings.
          </PlannerSubtle>
        </PlannerCard>
      </PlannerPage>
    );
  }

  return (
    <PlannerPage>
      <Tabs>
        <TabButton
          type="button"
          $active={activeTab === 'roles'}
          onClick={() => setActiveTab('roles')}
          disabled={!canManageScopedTeams}
        >
          Roles
        </TabButton>
        <TabButton
          type="button"
          $active={activeTab === 'service-plan'}
          onClick={() => setActiveTab('service-plan')}
        >
          Service Plan
        </TabButton>
      </Tabs>

      {activeTab === 'roles' ? (
        <SectionGrid>
          <PlannerCard style={{ gridColumn: '1 / -1' }}>
            <PlannerKicker>Team Coverage</PlannerKicker>
            <PlannerHeading>Teams and Required Roles</PlannerHeading>
            <PlannerSubtle>
              Team health is measured by required roles filled on upcoming services. One person never counts
              as 100% team health by themselves.
            </PlannerSubtle>
            <TeamGrid style={{ marginTop: '0.75rem' }}>
              {requiredCoverageByTeam.map((entry) => {
                const teamRoles = rolesByTeam.get(entry.team.id) ?? [];
                const requiredRows = entry.requiredRows;
                const requiredRoleIds = new Set(requiredRows.map((row) => row.role_id));
                const availableToAdd = teamRoles.filter((role) => !requiredRoleIds.has(role.id));

                return (
                  <TeamCard key={entry.team.id}>
                    <TeamHeader>
                      <TeamName>{entry.team.label}</TeamName>
                      <TeamHealth>{entry.coverage === null ? 'N/A' : `${entry.coverage}%`}</TeamHealth>
                    </TeamHeader>

                    <Track>
                      <Fill $value={entry.coverage ?? 0} />
                    </Track>

                    <PlannerSubtle style={{ marginTop: '0.45rem' }}>
                      Required coverage: {entry.fulfilledRequired} / {entry.requiredRoleIds.length}
                    </PlannerSubtle>

                    <MiniLabel style={{ display: 'block', marginTop: '0.6rem' }}>Team roles attached</MiniLabel>
                    <RolesList style={{ marginTop: '0.3rem' }}>
                      {teamRoles.length ? (
                        teamRoles.map((role) => (
                          <InlineRow key={role.id}>
                            <RoleChip>{role.name}</RoleChip>
                            {canManageScopedTeams ? (
                              <OutlineAction
                                type="button"
                                onClick={() => void handleDeleteRole(entry.team.id, role.id, role.name)}
                                disabled={planner.isMutating}
                              >
                                Remove
                              </OutlineAction>
                            ) : null}
                          </InlineRow>
                        ))
                      ) : (
                        <PlannerSubtle>No mapped roles yet.</PlannerSubtle>
                      )}
                    </RolesList>

                    <TeamControlRow>
                      <TeamControlInput
                        value={pendingRoleNameByTeam[entry.team.id] || ''}
                        disabled={!canManageScopedTeams || planner.isMutating}
                        placeholder={`New ${entry.team.label} role`}
                        onChange={(event) =>
                          setPendingRoleNameByTeam((prev) => ({
                            ...prev,
                            [entry.team.id]: event.target.value,
                          }))
                        }
                      />
                      <TeamControlButton
                        type="button"
                        onClick={() => handleCreateRole(entry.team.id)}
                        disabled={!canManageScopedTeams || !pendingRoleNameByTeam[entry.team.id]?.trim() || planner.isMutating}
                      >
                        Add Role
                      </TeamControlButton>
                    </TeamControlRow>

                    <TeamDivider />

                    <MiniLabel style={{ display: 'block', marginTop: '0.65rem' }}>Required role</MiniLabel>
                    <TeamControlRow>
                      <TeamControlSelect
                        value={pendingRequiredRoleByTeam[entry.team.id] || ''}
                        disabled={!canManageScopedTeams || !availableToAdd.length}
                        onChange={(event) =>
                          setPendingRequiredRoleByTeam((prev) => ({
                            ...prev,
                            [entry.team.id]: event.target.value,
                          }))
                        }
                      >
                        <option value="">Select role</option>
                        {availableToAdd.map((role) => (
                          <option key={role.id} value={role.id}>
                            {role.name}
                          </option>
                        ))}
                      </TeamControlSelect>
                      <TeamControlButton
                        type="button"
                        onClick={() => handleAddRequiredRole(entry.team.id)}
                        disabled={!canManageScopedTeams || !pendingRequiredRoleByTeam[entry.team.id] || planner.isMutating}
                      >
                        Add Required Role
                      </TeamControlButton>
                    </TeamControlRow>

                    <MiniLabel style={{ display: 'block', marginTop: '0.65rem' }}>Required roles</MiniLabel>
                    <AvailabilityList style={{ marginTop: '0.3rem' }}>
                      {requiredRows.length ? (
                        requiredRows.map((row) => {
                          const role = roleById.get(row.role_id);
                          return (
                            <RequiredRoleRow key={row.id}>
                              <RequiredRoleName>{role?.name || 'Unknown role'}</RequiredRoleName>
                              <OutlineAction
                                type="button"
                                disabled={!canManageRoles || planner.isMutating}
                                onClick={() => handleRemoveRequiredRole(row.id)}
                              >
                                Remove
                              </OutlineAction>
                            </RequiredRoleRow>
                          );
                        })
                      ) : (
                        <PlannerSubtle>No required roles set.</PlannerSubtle>
                      )}
                    </AvailabilityList>

                    <TeamDivider />

                    <MiniLabel style={{ display: 'block', marginTop: '0.65rem' }}>Team admins</MiniLabel>
                    <RolesList style={{ marginTop: '0.3rem' }}>
                      {(teamManagerRowsByTeam.get(entry.team.id) ?? []).length ? (
                        (teamManagerRowsByTeam.get(entry.team.id) ?? []).map((row) => {
                          const person = peopleById.get(row.user_id);
                          const label = person?.name || person?.email || row.user_id;
                          return (
                            <InlineRow key={row.id}>
                              <RoleChip>{label}</RoleChip>
                              {canGlobalPlannerAdmin ? (
                                <OutlineAction
                                  type="button"
                                  disabled={planner.isMutating}
                                  onClick={() => handleRemoveTeamManager(entry.team.id, row.user_id)}
                                >
                                  Remove
                                </OutlineAction>
                              ) : null}
                            </InlineRow>
                          );
                        })
                      ) : (
                        <PlannerSubtle>No team admins assigned.</PlannerSubtle>
                      )}
                    </RolesList>

                    {canGlobalPlannerAdmin ? (
                      <InlineRow style={{ marginTop: '0.65rem' }}>
                        <Select
                          value={pendingTeamManagerByTeam[entry.team.id] || ''}
                          disabled={planner.isMutating || !planner.people.length}
                          onChange={(event) =>
                            setPendingTeamManagerByTeam((prev) => ({
                              ...prev,
                              [entry.team.id]: event.target.value,
                            }))
                          }
                        >
                          <option value="">Select planner person</option>
                          {planner.people.map((person) => (
                            <option key={person.id} value={person.id}>
                              {person.name}
                            </option>
                          ))}
                        </Select>
                        <Action
                          type="button"
                          onClick={() => handleAddTeamManager(entry.team.id)}
                          disabled={!pendingTeamManagerByTeam[entry.team.id] || planner.isMutating}
                        >
                          Add Admin
                        </Action>
                      </InlineRow>
                    ) : null}
                  </TeamCard>
                );
              })}
            </TeamGrid>
          </PlannerCard>
        </SectionGrid>
      ) : (
        <SectionGrid>
          <PlannerCard>
            <PlannerKicker>Templates</PlannerKicker>
            <PlannerHeading>Service Plan Templates</PlannerHeading>
            <PlannerSubtle>
              Create, save, and apply templates directly in admin.
            </PlannerSubtle>

            <Field style={{ marginTop: '0.65rem' }}>
              Create Template Name
              <Input
                value={newTemplateName}
                onChange={(event) => setNewTemplateName(event.target.value)}
                disabled={!canCreatePlans && !canManageRoles}
                placeholder="Sunday Morning Core"
              />
            </Field>
            <Field style={{ marginTop: '0.55rem' }}>
              Create Template Description
              <Textarea
                value={newTemplateDescription}
                onChange={(event) => setNewTemplateDescription(event.target.value)}
                disabled={!canCreatePlans && !canManageRoles}
                placeholder="Optional notes for this reusable template"
              />
            </Field>
            <Action
              type="button"
              style={{ marginTop: '0.6rem' }}
              onClick={handleCreateTemplate}
              disabled={(!canCreatePlans && !canManageRoles) || planner.isMutating}
            >
              {planner.isMutating ? 'Saving...' : 'Create Template'}
            </Action>

            <Field style={{ marginTop: '0.8rem' }}>
              Save Template From Service Plan
              <Select
                value={saveTemplateSourcePlanId}
                onChange={(event) => setSaveTemplateSourcePlanId(event.target.value)}
                disabled={(!canCreatePlans && !canManageRoles) || !planner.plans.length}
              >
                <option value="">Select source service plan</option>
                {planner.plans.map((plan) => (
                  <option key={plan.id} value={plan.id}>{plan.title}</option>
                ))}
              </Select>
            </Field>
            <Field style={{ marginTop: '0.55rem' }}>
              Template Name (optional override)
              <Input
                value={saveTemplateName}
                onChange={(event) => setSaveTemplateName(event.target.value)}
                disabled={!canCreatePlans && !canManageRoles}
                placeholder="Leave blank to auto-name"
              />
            </Field>
            <Field style={{ marginTop: '0.55rem' }}>
              Template Description
              <Textarea
                value={saveTemplateDescription}
                onChange={(event) => setSaveTemplateDescription(event.target.value)}
                disabled={!canCreatePlans && !canManageRoles}
              />
            </Field>
            <Action
              type="button"
              style={{ marginTop: '0.6rem' }}
              onClick={handleSaveTemplateFromService}
              disabled={(!canCreatePlans && !canManageRoles) || !saveTemplateSourcePlanId || planner.isMutating}
            >
              {planner.isMutating ? 'Saving...' : 'Save From Service Plan'}
            </Action>

            <Field style={{ marginTop: '0.8rem' }}>
              Apply Template
              <Select
                value={applyTemplateId}
                onChange={(event) => setApplyTemplateId(event.target.value)}
                disabled={(!canCreatePlans && !canManageRoles) || !planner.serviceTemplates.length}
              >
                <option value="">Select template</option>
                {planner.serviceTemplates.map((template) => (
                  <option key={template.id} value={template.id}>{template.name}</option>
                ))}
              </Select>
            </Field>
            <Field style={{ marginTop: '0.55rem' }}>
              Target Service Plan
              <Select
                value={applyTemplateTargetPlanId}
                onChange={(event) => setApplyTemplateTargetPlanId(event.target.value)}
                disabled={(!canCreatePlans && !canManageRoles) || !planner.plans.length}
              >
                <option value="">Select target service plan</option>
                {planner.plans.map((plan) => (
                  <option key={plan.id} value={plan.id}>{plan.title}</option>
                ))}
              </Select>
            </Field>
            <Action
              type="button"
              style={{ marginTop: '0.6rem' }}
              onClick={handleApplyTemplateToService}
              disabled={
                (!canCreatePlans && !canManageRoles) ||
                !applyTemplateId ||
                !applyTemplateTargetPlanId ||
                planner.isMutating
              }
            >
              {planner.isMutating ? 'Applying...' : 'Apply Template To Service'}
            </Action>

            <PlannerSubtle style={{ marginTop: '0.8rem' }}>Active templates:</PlannerSubtle>
            <RolesList style={{ marginTop: '0.65rem' }}>
              {planner.serviceTemplates.length ? (
                planner.serviceTemplates.map((template) => (
                  <InlineRow key={template.id}>
                    <RoleChip>{template.name}</RoleChip>
                    {(canCreatePlans || canManageRoles) ? (
                      <OutlineAction
                        type="button"
                        onClick={() => void handleDeleteTemplate(template.id, template.name)}
                        disabled={planner.isMutating}
                      >
                        Delete
                      </OutlineAction>
                    ) : null}
                  </InlineRow>
                ))
              ) : (
                <PlannerSubtle>No active templates yet.</PlannerSubtle>
              )}
            </RolesList>
          </PlannerCard>

          <PlannerCard>
            <PlannerKicker>Integrations</PlannerKicker>
            <PlannerHeading>ProPresenter 7 Sync</PlannerHeading>
            <PlannerSubtle>
              Configure bridge credentials, schedule, and manual sync for two-way Service Planner sync.
            </PlannerSubtle>

            {ppConnection ? (
              <>
                <InlineRow style={{ marginTop: '0.7rem' }}>
                  <IntegrationStatusBadge $tone={statusTone}>
                    {ppConnection.status || 'disconnected'}
                  </IntegrationStatusBadge>
                  <RoleChip>{ppEnabled ? 'Enabled' : 'Disabled'}</RoleChip>
                  <RoleChip>
                    Last heartbeat: {ppConnection.last_heartbeat_at ? formatDateTime(ppConnection.last_heartbeat_at) : 'Never'}
                  </RoleChip>
                </InlineRow>

                <Field style={{ marginTop: '0.65rem' }}>
                  Sync interval (minutes)
                  <Input
                    value={ppSyncIntervalMinutes}
                    onChange={(event) => setPpSyncIntervalMinutes(event.target.value)}
                    disabled={ppSaving}
                    type="number"
                    min={1}
                    max={60}
                  />
                </Field>

                <InlineRow style={{ marginTop: '0.6rem' }}>
                  <input
                    id="pp-enabled-toggle"
                    type="checkbox"
                    checked={ppEnabled}
                    onChange={(event) => setPpEnabled(event.target.checked)}
                    disabled={ppSaving}
                  />
                  <MiniLabel>
                    Enable automatic scheduler + bridge sync
                  </MiniLabel>
                </InlineRow>

                <Field style={{ marginTop: '0.65rem' }}>
                  Bridge token (rotation)
                  <Input
                    value={ppBridgeToken}
                    onChange={(event) => setPpBridgeToken(event.target.value)}
                    disabled={ppSaving}
                    placeholder="Generate and copy to FOH bridge env"
                  />
                </Field>
                <InlineRow style={{ marginTop: '0.6rem' }}>
                  <OutlineAction type="button" onClick={generateBridgeToken} disabled={ppSaving}>
                    Generate Token
                  </OutlineAction>
                  <Action type="button" onClick={saveProPresenterConnection} disabled={ppSaving}>
                    {ppSaving ? 'Saving...' : 'Save Connection'}
                  </Action>
                  <Action type="button" onClick={runProPresenterSyncNow} disabled={ppSyncing || ppSaving || !ppEnabled}>
                    {ppSyncing ? 'Queueing...' : 'Sync Now'}
                  </Action>
                </InlineRow>

                <PlannerSubtle style={{ marginTop: '0.8rem' }}>
                  Recent sync runs
                </PlannerSubtle>
                <AvailabilityList style={{ marginTop: '0.5rem' }}>
                  {ppLoading ? (
                    <PlannerSubtle>Loading sync history...</PlannerSubtle>
                  ) : ppRuns.length === 0 ? (
                    <PlannerSubtle>No sync runs yet.</PlannerSubtle>
                  ) : (
                    ppRuns.map((run) => (
                      <AvailabilityRow key={run.id}>
                        <InlineRow>
                          <RoleChip>{run.trigger_source}</RoleChip>
                          <RoleChip>{run.direction}</RoleChip>
                          <IntegrationStatusBadge
                            $tone={run.status === 'completed' ? 'ok' : run.status === 'running' || run.status === 'queued' ? 'warn' : 'bad'}
                          >
                            {run.status}
                          </IntegrationStatusBadge>
                        </InlineRow>
                        <PlannerSubtle>
                          {run.queued_at ? formatDateTime(run.queued_at) : 'Unknown time'} • songs +{run.songs_inserted || 0}/{run.songs_updated || 0} • services +{run.services_inserted || 0}/{run.services_updated || 0} • conflicts {run.conflict_count || 0}
                        </PlannerSubtle>
                      </AvailabilityRow>
                    ))
                  )}
                </AvailabilityList>

                <PlannerSubtle style={{ marginTop: '0.8rem' }}>
                  Open conflicts (latest-edit-wins fallback)
                </PlannerSubtle>
                <AvailabilityList style={{ marginTop: '0.5rem' }}>
                  {ppConflicts.length === 0 ? (
                    <PlannerSubtle>No open conflicts.</PlannerSubtle>
                  ) : (
                    ppConflicts.map((conflict) => (
                      <AvailabilityRow key={conflict.id}>
                        <InlineRow>
                          <RoleChip>{conflict.entity_type}</RoleChip>
                          <RoleChip>{conflict.entity_key}</RoleChip>
                          <RoleChip>Winner: {conflict.winner}</RoleChip>
                        </InlineRow>
                        <PlannerSubtle>{conflict.resolution_reason || 'No reason captured.'}</PlannerSubtle>
                      </AvailabilityRow>
                    ))
                  )}
                </AvailabilityList>
              </>
            ) : (
              <PlannerSubtle style={{ marginTop: '0.7rem' }}>
                {ppLoading ? 'Loading ProPresenter connection...' : 'No ProPresenter connection row found.'}
              </PlannerSubtle>
            )}
          </PlannerCard>

          <PlannerCard>
            <PlannerKicker>Availability</PlannerKicker>
            <PlannerHeading>Upcoming Service Availability</PlannerHeading>
            <PlannerSubtle>
              Admins and team leaders can update any member. Others can only update their own availability.
            </PlannerSubtle>
            <Field style={{ marginTop: '0.65rem' }}>
              Planner Member
              <Select
                value={selectedAvailabilityPersonId}
                onChange={(event) => setSelectedAvailabilityPersonId(event.target.value)}
                disabled={!filteredPlannerPeople.length}
              >
                {filteredPlannerPeople.map((person) => (
                  <option key={person.id} value={person.id}>
                    {person.name}
                  </option>
                ))}
              </Select>
            </Field>

            {!selectedAvailabilityPerson ? (
              <PlannerSubtle style={{ marginTop: '0.7rem' }}>No planner member selected.</PlannerSubtle>
            ) : (
              <AvailabilityList style={{ marginTop: '0.75rem' }}>
                {planner.upcomingPlans.slice(0, 10).map((plan) => {
                  const draft = availabilityDraft[plan.id] || { status: '', notes: '' };
                  const canEditSelectedPerson =
                    canManageAvailability || selectedAvailabilityPerson.id === user?.id;

                  return (
                    <AvailabilityRow key={plan.id}>
                      <PlannerSubtle style={{ margin: 0, color: plannerColors.text }}>{plan.title}</PlannerSubtle>
                      <PlannerSubtle>{formatDateTime(plan.service_date)}</PlannerSubtle>
                      <InlineRow style={{ marginTop: '0.55rem' }}>
                        <Select
                          value={draft.status}
                          disabled={!canEditSelectedPerson}
                          onChange={(event) =>
                            setAvailabilityDraft((prev) => ({
                              ...prev,
                              [plan.id]: {
                                status: event.target.value,
                                notes: prev[plan.id]?.notes ?? draft.notes,
                              },
                            }))
                          }
                        >
                          <option value="">Available (default)</option>
                          <option value="available">Available</option>
                          <option value="maybe">Maybe</option>
                          <option value="unavailable">Unavailable</option>
                        </Select>
                        <Input
                          value={draft.notes}
                          disabled={!canEditSelectedPerson}
                          placeholder="Notes (optional)"
                          onChange={(event) =>
                            setAvailabilityDraft((prev) => ({
                              ...prev,
                              [plan.id]: {
                                status: prev[plan.id]?.status ?? draft.status,
                                notes: event.target.value,
                              },
                            }))
                          }
                        />
                        <Action
                          type="button"
                          disabled={!canEditSelectedPerson || planner.isMutating}
                          onClick={() => handleSaveAvailability(plan.id)}
                        >
                          Save
                        </Action>
                      </InlineRow>
                    </AvailabilityRow>
                  );
                })}
              </AvailabilityList>
            )}
          </PlannerCard>

          <PlannerCard>
            <PlannerKicker>Analytics</PlannerKicker>
            <PlannerHeading>Service Plan Summary</PlannerHeading>
            <Score>{assignmentOverallCoverage}%</Score>
            <PlannerSubtle>
              Assignment coverage from upcoming plans based on assigned roles that are not declined/unavailable.
            </PlannerSubtle>
            <RolesList style={{ marginTop: '0.75rem' }}>
              {assignmentMetrics.map((metric) => (
                <RoleChip key={metric.roleId}>
                  {metric.label}: {metric.coverage}% ({metric.filledAssignments}/{metric.totalAssignments})
                </RoleChip>
              ))}
            </RolesList>
          </PlannerCard>
        </SectionGrid>
      )}

      {feedback ? <PlannerSubtle>{feedback}</PlannerSubtle> : null}
      {planner.isError || rolesQuery.isError ? <PlannerError>Unable to refresh planner data.</PlannerError> : null}
    </PlannerPage>
  );
}
